import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';

export interface ProtectedRouteProps {
  /** The content to render when access is granted */
  children: ReactNode;
  /** Required permission(s) — user must have ALL of these to access the route */
  permissions?: string[];
  /** Alternative permission check — user must have ANY of these to access the route */
  anyPermission?: string[];
  /** Required role(s) — user must have one of these roles */
  roles?: string[];
  /** Custom fallback component when permission is denied (defaults to redirect) */
  fallback?: ReactNode;
  /** Redirect path when not authenticated (defaults to /auth/login) */
  loginPath?: string;
  /** Redirect path when permission is denied (defaults to /unauthorized) */
  unauthorizedPath?: string;
}

/**
 * Protected route wrapper that enforces authentication and permission checks.
 * Redirects unauthenticated users to login and unauthorized users to an error page.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/billing"
 *   element={
 *     <ProtectedRoute permissions={[PERMISSIONS.BILLING_READ]}>
 *       <BillingPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * Validates: Requirements 2.1, 2.2
 */
export function ProtectedRoute({
  children,
  permissions,
  anyPermission,
  roles,
  fallback,
  loginPath = '/auth/login',
  unauthorizedPath = '/unauthorized',
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isInitialized, mfaStatus } = useAuthStore();
  const { can, canAny, role, isLoading: permissionsLoading } = usePermissions();

  // Wait for auth state to initialize before making decisions
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface dark:bg-surface-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // If MFA is required but not yet verified, redirect to login (MFA step)
  if (mfaStatus === 'required' || mfaStatus === 'verifying') {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Wait for permissions to load before checking access
  if (permissionsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface dark:bg-surface-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  // Check role-based access
  if (roles && roles.length > 0) {
    if (!roles.includes(role)) {
      if (fallback) return <>{fallback}</>;
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  // Check required permissions (ALL must be present)
  if (permissions && permissions.length > 0) {
    const hasAll = permissions.every((p) => can(p));
    if (!hasAll) {
      if (fallback) return <>{fallback}</>;
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  // Check any permissions (at least ONE must be present)
  if (anyPermission && anyPermission.length > 0) {
    if (!canAny(anyPermission)) {
      if (fallback) return <>{fallback}</>;
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  return <>{children}</>;
}
